import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { httpResource, HttpErrorResponse } from '@angular/common/http';
import { SortDirection } from '@angular/material/sort';
import { ParamMap, Router } from '@angular/router';
import { firstValueFrom, map, Observable, tap } from 'rxjs';

import { reloadOnCountChange } from '../../Shared/Services/reload-on-count-change.util';
import { CatalogOperationsFacade } from '../../../Facades/catalog-operations.facade';
import { MsgService } from '../../Shared/Services/msg.service';
import { ApplicationDemandsService } from '../Services/application-demands.service';
import { GroupDemandsService } from '../Services/group-demands.service';
import { DemandsListService } from '../Services/demands-list.service';
import { DemandCreatedToastService } from '../Services/demand-created-toast.service';
import { DemandCancelData } from '../../../Interfaces/api/applications/demand.interface';
import {
  MyDemandsListResponse,
  MyDemandsListRow,
} from '../../../Interfaces/api/catalog/my-demands-list.interface';
import { DemandSubjectType } from '../../../Enums/demand-subject-type.enum';
import { Platform } from '../../../Enums/platform.enum';
import { MergedDemandRow, MyDemandsFilters } from '../Interfaces/my-demands.interface';
import {
  MY_DEMANDS_DEFAULT_ORDER,
  MY_DEMANDS_DEFAULT_ORDER_BY,
  MY_DEMANDS_DEFAULT_PAGE,
  MY_DEMANDS_DEFAULT_PAGE_SIZE,
  MY_DEMANDS_DEFAULT_PLATFORM,
  MY_DEMANDS_DEFAULT_REQUESTED_FROM,
  MY_DEMANDS_DEFAULT_REQUESTED_TO,
  MY_DEMANDS_DEFAULT_SUBJECT_TAB,
  MY_DEMANDS_ORDER_BY_VALUES,
  MY_DEMANDS_ORDER_VALUES,
  MY_DEMANDS_STATUS_FILTER_MAP,
  MY_DEMANDS_STATUS_FILTERS,
  MY_DEMANDS_SUBJECT_TABS,
  MyDemandsStatusFilter,
  MyDemandsSubjectTab,
} from '../Consts/list-my-demands.const';
import { DEMANDS_SEARCH_MAX_LENGTH } from '../Consts/search-max-length.const';
import { ResourceStateService } from '../../Shared/Services/resource-state.service';
import {
  parseDateRangeParams,
  parseEnumParam,
  parseNullableEnumParam,
  parsePageParam,
  parseSearchParam,
} from '../Shared/query-param-parse.util';

@Injectable()
export class MyDemandsFacade {
  private readonly _appDemands = inject(ApplicationDemandsService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _groupDemands = inject(GroupDemandsService);
  private readonly _demandsList = inject(DemandsListService);
  private readonly _catalogOperations = inject(CatalogOperationsFacade);
  private readonly _msg = inject(MsgService);
  private readonly _createdToast = inject(DemandCreatedToastService);
  private readonly _router = inject(Router);

  readonly filters = signal<MyDemandsFilters>({
    status: 'all',
    search: '',
    page: MY_DEMANDS_DEFAULT_PAGE,
    pageSize: MY_DEMANDS_DEFAULT_PAGE_SIZE,
    orderBy: MY_DEMANDS_DEFAULT_ORDER_BY,
    order: MY_DEMANDS_DEFAULT_ORDER,
    requestedFrom: MY_DEMANDS_DEFAULT_REQUESTED_FROM,
    requestedTo: MY_DEMANDS_DEFAULT_REQUESTED_TO,
    platform: MY_DEMANDS_DEFAULT_PLATFORM,
  });

  readonly subjectTab = signal<MyDemandsSubjectTab>(MY_DEMANDS_DEFAULT_SUBJECT_TAB);

  readonly demands = httpResource<MyDemandsListResponse>(() =>
    this._demandsList.list({
      status: MY_DEMANDS_STATUS_FILTER_MAP[this.filters().status],
      search: this.filters().search || undefined,
      page: this.filters().page,
      pageSize: this.filters().pageSize,
      orderBy: this.filters().orderBy,
      order: this.filters().order,
      requestedFrom: this.filters().requestedFrom ?? undefined,
      requestedTo: this.filters().requestedTo ?? undefined,
      platform: this.filters().platform ?? undefined,
      subjectType: this.subjectTab(),
    }),
  );

  readonly merged: Signal<MergedDemandRow[]> = computed(() => {
    const rows = this._resourceState.valueOf(this.demands)?.data?.data ?? [];
    return rows.map((row) => this._toMergedRow(row));
  });

  readonly totalCount: Signal<number> = computed(
    () => this._resourceState.valueOf(this.demands)?.data?.count ?? 0,
  );

  readonly coldLoading: Signal<boolean> = computed(() => this.demands.status() === 'loading');

  readonly loading: Signal<boolean> = computed(() => this.demands.isLoading());

  readonly hasError: Signal<boolean> = computed(() => this.demands.error() !== undefined);

  readonly activeFilterCount: Signal<number> = computed(() => {
    const f = this.filters();
    let count = 0;
    if (f.status !== 'all') count++;
    if (f.requestedFrom || f.requestedTo) count++;
    if (f.platform) count++;
    return count;
  });

  readonly hasFilters: Signal<boolean> = computed(() => this.activeFilterCount() > 0);

  constructor() {
    this._registerCountRefresh();
  }

  private _registerCountRefresh(): void {
    reloadOnCountChange(
      () => this._catalogOperations.counts()?.my_demands_pending,
      () => this.reload(),
    );
  }

  reload(): void {
    this.demands.reload();
  }

  setFilters(partial: Partial<MyDemandsFilters>): void {
    this.filters.update((current) => ({ ...current, ...partial }));
  }

  setStatus(status: MyDemandsStatusFilter): void {
    this.filters.update((current) => ({ ...current, status, page: MY_DEMANDS_DEFAULT_PAGE }));
    this._pushFiltersToUrl(false);
  }

  setSubjectTab(tab: MyDemandsSubjectTab): void {
    if (tab === this.subjectTab()) return;
    this.subjectTab.set(tab);
    this.filters.update((current) => ({ ...current, page: MY_DEMANDS_DEFAULT_PAGE }));
    this._pushFiltersToUrl(false);
  }

  setSearch(term: string): void {
    const search = term.trim();
    if (search === this.filters().search) return;
    this.filters.update((current) => ({ ...current, search, page: MY_DEMANDS_DEFAULT_PAGE }));
    this._pushFiltersToUrl(false);
  }

  setPage(page: number): void {
    this.filters.update((current) => ({ ...current, page }));
    this._pushFiltersToUrl(false);
  }

  setSort(orderBy: string, order: SortDirection): void {
    this.filters.update((current) => ({
      ...current,
      orderBy: order ? orderBy : MY_DEMANDS_DEFAULT_ORDER_BY,
      order: order || MY_DEMANDS_DEFAULT_ORDER,
      page: MY_DEMANDS_DEFAULT_PAGE,
    }));
    this._pushFiltersToUrl(false);
  }

  setPageSize(pageSize: number): void {
    this.filters.update((current) => ({ ...current, pageSize, page: MY_DEMANDS_DEFAULT_PAGE }));
    this._pushFiltersToUrl(false);
  }

  setRequestedDateRange(requestedFrom: string | null, requestedTo: string | null): void {
    this.filters.update((current) => ({
      ...current,
      requestedFrom,
      requestedTo,
      page: MY_DEMANDS_DEFAULT_PAGE,
    }));
    this._pushFiltersToUrl(false);
  }

  setPlatform(platform: Platform | null): void {
    this.filters.update((current) => ({ ...current, platform, page: MY_DEMANDS_DEFAULT_PAGE }));
    this._pushFiltersToUrl(false);
  }

  clearFilters(): void {
    this.filters.update((current) => ({
      ...current,
      status: 'all',
      requestedFrom: MY_DEMANDS_DEFAULT_REQUESTED_FROM,
      requestedTo: MY_DEMANDS_DEFAULT_REQUESTED_TO,
      platform: MY_DEMANDS_DEFAULT_PLATFORM,
      page: MY_DEMANDS_DEFAULT_PAGE,
    }));
    this._pushFiltersToUrl(false);
  }

  syncFromQueryParams(params: ParamMap): boolean {
    const parsed = this._parseFiltersFromParams(params);
    return this._applyParsedFilters(parsed);
  }

  private _parseFiltersFromParams(params: ParamMap): {
    filters: MyDemandsFilters;
    subjectTab: MyDemandsSubjectTab;
    malformed: boolean;
  } {
    const status = parseEnumParam(params.get('status'), MY_DEMANDS_STATUS_FILTERS, 'all');
    const dateRange = parseDateRangeParams(params.get('requestedFrom'), params.get('requestedTo'));
    const search = parseSearchParam(params.get('search'), DEMANDS_SEARCH_MAX_LENGTH);
    const platform = parseNullableEnumParam(params.get('platform'), [
      Platform.Windows,
      Platform.Macos,
    ]);
    const page = parsePageParam(params.get('page'), MY_DEMANDS_DEFAULT_PAGE);
    const order = parseEnumParam(
      params.get('order'),
      MY_DEMANDS_ORDER_VALUES,
      MY_DEMANDS_DEFAULT_ORDER,
    );
    const subjectTab = parseEnumParam(
      params.get('subjectTab'),
      MY_DEMANDS_SUBJECT_TABS,
      MY_DEMANDS_DEFAULT_SUBJECT_TAB,
    );
    const orderBy = parseEnumParam(
      params.get('orderBy'),
      MY_DEMANDS_ORDER_BY_VALUES,
      MY_DEMANDS_DEFAULT_ORDER_BY,
    );

    const filters: MyDemandsFilters = {
      ...this.filters(),
      status: status.value,
      search: search.value,
      requestedFrom: dateRange.value.from,
      requestedTo: dateRange.value.to,
      platform: platform.value,
      page: page.value,
      orderBy: orderBy.value,
      order: order.value,
    };

    const malformed =
      status.malformed ||
      search.malformed ||
      dateRange.malformed ||
      platform.malformed ||
      page.malformed ||
      orderBy.malformed ||
      order.malformed ||
      subjectTab.malformed;

    return { filters, subjectTab: subjectTab.value, malformed };
  }

  private _applyParsedFilters(parsed: {
    filters: MyDemandsFilters;
    subjectTab: MyDemandsSubjectTab;
    malformed: boolean;
  }): boolean {
    const filtersChanged = !this._filtersEqual(this.filters(), parsed.filters);
    const subjectTabChanged = this.subjectTab() !== parsed.subjectTab;
    if (filtersChanged) {
      this.filters.set(parsed.filters);
    }
    if (subjectTabChanged) {
      this.subjectTab.set(parsed.subjectTab);
    }
    if (parsed.malformed) {
      this._pushFiltersToUrl(true);
    }
    return filtersChanged || subjectTabChanged;
  }

  private _filtersEqual(a: MyDemandsFilters, b: MyDemandsFilters): boolean {
    return (
      a.status === b.status &&
      a.search === b.search &&
      a.requestedFrom === b.requestedFrom &&
      a.requestedTo === b.requestedTo &&
      a.platform === b.platform &&
      a.page === b.page &&
      a.orderBy === b.orderBy &&
      a.order === b.order
    );
  }

  private _pushFiltersToUrl(replaceUrl: boolean): void {
    const f = this.filters();
    const tab = this.subjectTab();
    const queryParams: Record<string, string | number | null> = {
      subjectTab: tab === MY_DEMANDS_DEFAULT_SUBJECT_TAB ? null : tab,
      status: f.status === 'all' ? null : f.status,
      search: f.search.length > 0 ? f.search : null,
      requestedFrom: f.requestedFrom,
      requestedTo: f.requestedTo,
      platform: f.platform,
      page: f.page === MY_DEMANDS_DEFAULT_PAGE ? null : f.page,
      orderBy: f.orderBy === MY_DEMANDS_DEFAULT_ORDER_BY ? null : f.orderBy,
      order: f.order === MY_DEMANDS_DEFAULT_ORDER ? null : f.order,
    };
    void this._router.navigate(['/my-demands'], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  private _toMergedRow(row: MyDemandsListRow): MergedDemandRow {
    if (row.subject_type === DemandSubjectType.Application) {
      return { subjectType: DemandSubjectType.Application, row };
    }
    return { subjectType: DemandSubjectType.Group, row };
  }

  cancel(row: MergedDemandRow): Observable<DemandCancelData> {
    const id = row.row.id;
    const stream =
      row.subjectType === DemandSubjectType.Application
        ? this._appDemands.cancel(id)
        : this._groupDemands.cancel(id);

    return stream.pipe(
      tap((response) => {
        this.demands.reload();
        this._catalogOperations.reload();
        this._msg.showSnackBar(response.message, { type: 'success' });
      }),
      map((response) => response.data),
    );
  }

  async requestAgain(row: MergedDemandRow): Promise<{ needsDevice: boolean }> {
    if (row.subjectType !== DemandSubjectType.Application) return { needsDevice: false };
    const applicationId = row.row.application?.id;
    if (!applicationId) return { needsDevice: false };
    try {
      const response = await firstValueFrom(this._appDemands.store(applicationId, {}, false, true));
      this.demands.reload();
      this._catalogOperations.reload();
      this._createdToast.announce(response);
      return { needsDevice: false };
    } catch (error) {
      if (this._isDeviceRequiredError(error)) {
        return { needsDevice: true };
      }
      this._msg.showSnackBar('app.catalog.demandDetail.error.requestAgainFailed', {
        type: 'error',
      });
      return { needsDevice: false };
    }
  }

  async requestAgainWithDevice(row: MergedDemandRow, deviceIntuneId: string): Promise<void> {
    if (row.subjectType !== DemandSubjectType.Application) return;
    const applicationId = row.row.application?.id;
    if (!applicationId) return;
    try {
      const response = await firstValueFrom(
        this._appDemands.store(applicationId, { device_intune_id: deviceIntuneId }),
      );
      this.demands.reload();
      this._catalogOperations.reload();
      this._createdToast.announce(response);
    } catch {
      this._msg.showSnackBar('app.catalog.demandDetail.error.requestAgainFailed', {
        type: 'error',
      });
    }
  }

  private _isDeviceRequiredError(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status === 422 &&
      !!error.error?.errors?.device_intune_id
    );
  }
}
